import React from 'react';
import { Flex } from '@components/Flex';
import { FooterMenu } from './FooterMenu';
import { FooterSocial } from './FooterSocial';
import { getRootUrl } from '@utils/urlHelpers.util';
import { FooterPrivacyMenu } from './FooterPrivacyMenu';
import { footerPageId } from 'src/layout/components/Header/Header.utils';
import { useShopStorefrontContext } from '@utils/storefront/ShopStorefrontProvider';
import { CookiesConsentBanner, CookiesConsentBannerStyles } from 'src/layout/CookiesConsentBanner';
import {
  FooterMenuContainer,
  FooterColumnContainer,
  FooterColumns,
  FooterColumn,
  FooterColumnBottom,
  FooterMenuCTALink,
  FooterSubMenuCTALink,
  FooterMenuWrapper,
  FooterSubMenuWrapper,
  UniversalFooter,
  UniversalFooterLink,
  NewFlagText,
  FooterNavButton,
  FooterNavButtonContent,
  UniversalFooterMenuWrapper,
  StyledGetStartedButton,
  StyledFindEventButton,
  CopyrightText,
  FooterCopyrightContainer,
  UniversalFooterContainer,
  FooterMenuDisplayAboveMobile,
  UniversalCtaContainer,
  StyledParticleJoyLogo,
  FooterMenuDisplayOnMobile,
  UniversalFooterSocialMenuWrapper,
  PrivacyLinksWrapper,
  UniversalFooterWrapper,
  UniversalFooterLogoContainer,
  FooterSubMenuContainer,
  StyledFooter,
  TradeMarkContainer,
  TradeMarkText,
  ContentWrapper,
  StyledTextCarousel,
  WithJoyText,
  TrademarkSymbol,
  LanguageMenuContainer,
} from './Footer.styles';
import { CatalogStorefront } from '@utils/storefront/storefront.types';
import { useTranslation } from 'react-i18next';
import { LanguageMenu } from '@components/LanguageMenu';

export const Footer: React.FC<{ page?: MarcomPageName; handleGetStarted?: (e: React.MouseEvent) => void }> = ({
  page,
  handleGetStarted,
}) => {
  const { config, currentStorefront } = useShopStorefrontContext();

  const { t } = useTranslation('header');

  return (
    <StyledFooter id={footerPageId({ page })}>
      <FooterMenuContainer>
        <FooterColumnContainer>
          <FooterColumns>
            <FooterColumn desktop="half" mobile="full">
              <FooterMenuWrapper flexDirection="column">
                {config.footerMainMenuOptions.map((menu) => (
                  <FooterMenuCTALink
                    key={menu.id}
                    id={menu.id}
                    href={menu.to}
                    target={menu.target}
                    variant="footerAltDefault"
                    typography={['hed32Mobile', 'hed72Hero']}
                  >
                    {menu.title}
                  </FooterMenuCTALink>
                ))}
              </FooterMenuWrapper>
            </FooterColumn>
            <FooterColumn desktop="half" mobile="full">
              <FooterSubMenuContainer>
                <FooterSubMenuWrapper justifyContent="flex-start" flexDirection="column">
                  {config.footerMenuOptions.map((menu) => (
                    <FooterSubMenuCTALink
                      key={menu.id}
                      id={menu.id}
                      href={menu.to}
                      target={menu.target}
                      variant="footerAltDefault"
                      typography="dek20"
                    >
                      {menu.title}
                    </FooterSubMenuCTALink>
                  ))}
                </FooterSubMenuWrapper>
              </FooterSubMenuContainer>
            </FooterColumn>
          </FooterColumns>
        </FooterColumnContainer>
      </FooterMenuContainer>
      <TradeMarkContainer>
        <ContentWrapper>
          <TradeMarkText>
            <StyledTextCarousel content={t('footer.tradeMarkContent')} />
          </TradeMarkText>
          <WithJoyText>
            With{'\u00A0'}Joy<TrademarkSymbol>™</TrademarkSymbol>
          </WithJoyText>
        </ContentWrapper>
      </TradeMarkContainer>
      <UniversalFooterWrapper backgroundColor="mono1">
        <FooterColumnContainer>
          <FooterColumns>
            <FooterColumnBottom width="full">
              <UniversalFooter flexDirection="column" paddingY={[8, 9]}>
                <UniversalFooterContainer justifyContent="space-between">
                  <UniversalFooterMenuWrapper>
                    <FooterNavButton
                      active={currentStorefront === CatalogStorefront.wedding}
                      id="universalFooterMenuWedding"
                      forwardedAs={'a'}
                      href={getRootUrl('/')}
                    >
                      {t('universalNav.joyWedding')}
                    </FooterNavButton>
                    <FooterNavButton
                      active={currentStorefront === CatalogStorefront.baby}
                      id="universalFooterMenuBabyRegistry"
                      forwardedAs={'a'}
                      href={getRootUrl('/baby-registry/')}
                    >
                      <FooterNavButtonContent>
                        <span>{t('universalNav.joyBaby')}</span>
                        <NewFlagText tagName="span">{t('universalNav.new')}</NewFlagText>
                      </FooterNavButtonContent>
                    </FooterNavButton>
                  </UniversalFooterMenuWrapper>
                  <FooterMenuDisplayAboveMobile>
                    <FooterMenu id="Desktop" />
                    <LanguageMenuContainer style={{ marginLeft: '32px' }}>
                      <LanguageMenu />
                    </LanguageMenuContainer>
                  </FooterMenuDisplayAboveMobile>
                </UniversalFooterContainer>
                <UniversalFooterContainer justifyContent="space-between">
                  <UniversalCtaContainer>
                    <StyledGetStartedButton
                      id="universalFooterGetStarted"
                      page={page}
                      variant="ghost"
                      rounded
                      iconRight="forwardChevron"
                      onClick={handleGetStarted}
                    >
                      {t('footer.getStarted')}
                    </StyledGetStartedButton>
                    <StyledFindEventButton
                      id="universalFooterFindAnEvent"
                      variant="ghost"
                      rounded
                      iconLeft="search"
                      iconSize={24}
                      forwardedAs="a"
                      href={getRootUrl('/find/')}
                    >
                      {t('footer.findAnEvent')}
                    </StyledFindEventButton>
                  </UniversalCtaContainer>
                  <FooterMenuDisplayOnMobile>
                    <FooterMenu id="Mobile" />
                  </FooterMenuDisplayOnMobile>
                  <UniversalFooterLogoContainer alignItems={['flex-start', 'center']}>
                    <UniversalFooterSocialMenuWrapper justifyContent="center">
                      <FooterSocial />
                    </UniversalFooterSocialMenuWrapper>
                    <StyledParticleJoyLogo id="universalFooterLogo" variant="primary" ariaLabel="Footer Joy Logo" />
                  </UniversalFooterLogoContainer>
                </UniversalFooterContainer>
                <FooterCopyrightContainer justifyContent="space-between" paddingTop={3}>
                  <CopyrightText variant="navParent18" tagName="span">
                    © {new Date().getFullYear()} Joy. {t('footer.copyright')}
                    <CookiesConsentBanner id="Desktop" />
                  </CopyrightText>
                  <PrivacyLinksWrapper>
                    <FooterPrivacyMenu id="Footer" />
                  </PrivacyLinksWrapper>
                </FooterCopyrightContainer>
              </UniversalFooter>
            </FooterColumnBottom>
          </FooterColumns>
        </FooterColumnContainer>
      </UniversalFooterWrapper>
      <CookiesConsentBannerStyles />
    </StyledFooter>
  );
};
